import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApplicationDetailsService } from "../../loan-applications/loan-application-details/loan-application-details.service";
import { LoanApplicationService } from "../../loan-applications/loan-applications.service";
import { LoanApplicationItem, LoanApplicationUpdateDTO } from "../../loan-applications/loan-applications.interface";
import { UserLoginService } from "src/app/login/userlogin.service";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { LosStateService } from "../los-state.service";

@Component({
  selector: "app-user-consent",
  templateUrl: "./user-consent.component.html",
  styleUrls: ["./user-consent.component.css"],
})
export class UserConsentComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private applicationDetailsService = inject(ApplicationDetailsService);
  private loanApplicationService = inject(LoanApplicationService);
  userLoginService = inject(UserLoginService);
  private losState = inject(LosStateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;
  userKycData: any | null = null;

  c1 = false;
  c2 = false;
  c3 = false;

  timestamp: string | null = null;
  ip: string | null = null;
  isSubmitting = false;

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);

    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(kyc => this.userKycData = kyc);

    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (details && details.length > 0) {
          const consent = details.find(d => d.parameterName === 'User Consent')?.value;
          if (consent === 'Accepted') {
            this.c1 = true;
            this.c2 = true;
            this.c3 = true;
          }
        }
      });
  }

  get allChecked(): boolean {
    return this.c1 && this.c2 && this.c3;
  }

  sendConsentLink(): void {
    this.toastr.info('Consent link sent to borrower mobile');
  }

  async submitConsent(): Promise<void> {
    if (!this.allChecked) {
      this.toastr.warning('Please accept all consent checkboxes before proceeding.');
      return;
    }
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    this.timestamp = new Date().toLocaleString();
    this.ip = 'Web-Client';

    const appId = this.applicationId;
    if (!appId) {
      this.toastr.warning('Application ID missing. Cannot save consent.');
      this.isSubmitting = false;
      return;
    }

    try {
      const actionUser = this.userLoginService.User()?.userId?.toString() || '0';

      // Persist consent timestamp
      await firstValueFrom(this.applicationDetailsService.ApplicationDetails_UpdateByParameterName({
        applicationId: appId,
        parameterName: 'User Consent',
        value: 'Accepted',
        isVerified: 1,
        verificationSource: 'Customer_Web',
        actionUser: actionUser
      }));

      await firstValueFrom(this.applicationDetailsService.ApplicationDetails_UpdateByParameterName({
        applicationId: appId,
        parameterName: 'Consent Timestamp',
        value: this.timestamp,
        isVerified: 1,
        verificationSource: 'Customer_Web',
        actionUser: actionUser
      }));

      // Refresh application details state
      this.losState.refreshDetails(appId);

      // Update application status to KYCPending
      if (this.applicationData) {
        const updatePayload: LoanApplicationUpdateDTO = {
          applicationId: this.applicationData.applicationId,
          userId: this.applicationData.userId,
          productId: this.applicationData.productId,
          applicationStatus: 'KYCPending',
          requestedAmount: this.applicationData.requestedAmount,
          approvedAmount: this.applicationData.approvedAmount,
          tenureMonths: this.applicationData.tenureMonths,
          applicationDate: this.applicationData.applicationDate,
          approvalDate: this.applicationData.approvalDate,
          approvedBy: this.applicationData.approvedBy || '',
          rejectionReason: this.applicationData.rejectionReason || '',
          rejectedBy: this.applicationData.rejectedBy || '',
          isDisbursed: this.applicationData.isDisbursed,
          disbursedOn: this.applicationData.disbursedOn,
          disbursedAmount: this.applicationData.disbursedAmount,
          subUserId: this.applicationData.subUserId,
          businessId: this.applicationData.businessId,
          productCode: this.applicationData.productCode || '',
          prrn: this.applicationData.prrn,
          lmsApplicationName: this.applicationData.lmsApplicationName,
          otherDetail1: this.applicationData.otherDetail1,
          otherDetail2: this.applicationData.otherDetail2,
          otherDetail3: this.applicationData.otherDetail3,
          otherDetail4: this.applicationData.otherDetail4,
          otherDetail5: this.applicationData.otherDetail5,
          otherDetail6: this.applicationData.otherDetail6,
          isActive: 1,
          actionUser: actionUser
        };
        const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(updatePayload));
        this.losState.patchApplication(updated);
      }

      this.toastr.success('Consent recorded successfully');
      this.isSubmitting = false;
      this.next.emit();
    } catch (err) {
      this.toastr.error('Failed to record consent');
      console.error('Consent error:', err);
      this.isSubmitting = false;
    }
  }
}
